/**
 * @module app.ext.iqc
 */

import { autoinject, bindable, computedFrom, transient } from 'aurelia-framework';
import { IOpenIQCParameters, IqcManager } from 'app/ext/iqc/iqc-manager';
import { AuthContext } from 'app/um/lib/auth-context';
import { I18N } from 'aurelia-i18n';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

export interface IExtIqcSettings {
    allowOpen?: boolean;
    showOpenReviewSummaryPdf?: boolean;
    showOpenBookView?: boolean;
    allowReviewCopy?: boolean;
    useLatest?: boolean;
    useDynamicSso?: boolean;
}

@transient()
@autoinject()
export class ExtIqc {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected diagnosis: UM.AuthDiagnosis | null = null;
    @bindable
    protected service: UM.AuthService | null = null;
    @bindable
    protected careDay: UM.AdmissionCareDay | null = null;

    @bindable
    protected readonly: boolean = false;

    @bindable
    protected settings: IExtIqcSettings = {};

    @bindable
    protected showOpen: boolean = true;

    @bindable
    protected review: UM.AuthMedicalReview | null = null;

    protected auth: UM.Auth | null = null;


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(protected iqc: IqcManager,
        protected i18n: I18N,
        private context: AuthContext) { }

    protected bind(): void {
        if(!this.auth) this.auth = this.context.auth;

        if(!this.review) {
            if(this.diagnosis) this.review = this.diagnosis.medicalReview || null;
            if(this.service) this.review = this.service.medicalReview || null;
            if(this.careDay) this.review = this.careDay.authMedicalReview || null;
        }

        // configure fallback settings
        this.settings = {
            allowOpen: !isNullOrUndefined(this.settings?.allowOpen) ? this.settings.allowOpen : false,
            showOpenReviewSummaryPdf: !isNullOrUndefined(this.settings?.showOpenReviewSummaryPdf) ? this.settings.showOpenReviewSummaryPdf : false,
            showOpenBookView: !isNullOrUndefined(this.settings?.showOpenBookView) ? this.settings.showOpenBookView : true,
            allowReviewCopy: !isNullOrUndefined(this.settings?.allowReviewCopy) ? this.settings.allowReviewCopy : false,
            useLatest: !isNullOrUndefined(this.settings?.useLatest) ? this.settings.useLatest : false,
            useDynamicSso: !isNullOrUndefined(this.settings?.useDynamicSso) ? this.settings.useDynamicSso : false
        };
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected open(): void {
        const openIqcParameters: IOpenIQCParameters = {
            auth: this.auth ?? undefined,
            review: this.review ?? undefined,
            allowReviewCopy: this.settings.allowReviewCopy
        };

        if(this.review) {
            this.iqc.open(openIqcParameters);
        } else if(this.diagnosis) {
            openIqcParameters.diagnosis = this.diagnosis;
            this.iqc.open(openIqcParameters);
        } else if(this.service) {
            openIqcParameters.services = [this.service];
            this.iqc.open(openIqcParameters);
        } else if(this.careDay) {
            openIqcParameters.careDay = this.careDay;
            this.iqc.open(openIqcParameters);
        } else {
            this.iqc.open({});
        }
    }

    protected openReviewSummaryPdf(): void {
        if(!this.review) return;
        const openIqcParameters: IOpenIQCParameters = {
            auth: this.auth && this.settings.useDynamicSso ? this.auth : undefined,
            review: this.review,
            openReviewSummaryPdf: true,
            useDynamicSso: this.settings.useDynamicSso ?? false
        };
        this.iqc.open(openIqcParameters);
    }

    protected getIcon(review: UM.AuthMedicalReview): string {
        if(review) {
            if(review.complete) {
                return "./app/ext/icons/IQ-check.svg";
            } else {
                return "./app/ext/icons/IQ-save.svg";
            }
        } else {
            return "./app/ext/icons/IQ.svg";
        }
    }

    protected getTooltip(): string {
        // If review exists, action label is "open". If review does not exist, action label is "start".
        let action = this.review ? this.i18n.tr('ui:actions.open') : this.i18n.tr('ui:actions.start');

        // If allowing review copy and the review is complete, set action label for "copy".
        if(this.settings.allowReviewCopy && this.review?.complete) {
            action = this.i18n.tr('ui:actions.copy')
        }

        return this.i18n.tr('ext:iqc.navigation.reviewWithCode', { what: `${this.getSource}`, item: `${this.getCode}`, action });
    }

    @computedFrom('diagnosis', 'service')
    protected get getCode(): string {
        if(this.diagnosis) {
            return this.diagnosis.definition?.code ?? '';
        } else if(this.service) {
            return this.service.definition?.code ?? '';
        } else return '';
    }

    @computedFrom('diagnosis', 'service', 'careDay')
    protected get getSource(): string {
        if(this.diagnosis) {
            return this.i18n.tr('um:auth.diagnosis.code.$long');
        } else if(this.service) {
            return this.i18n.tr('um:service.code.$long');
        } else if(this.careDay) {
            return this.i18n.tr('um:auth.careDays.$');
        } else {
            return this.i18n.tr('um:service.code.$');
        }
    }

    ////****************** Service Calls ********************************************////

}
